import { Injectable } from '@angular/core';

import { OddsAcceptanceMode } from '@bpos';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { CustomLoadingIndicatorService, ILoadingIndicatorHandler } from '@frontend/sports/loading-indicator-feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { SyncUserSettingsService, UserSettingsService } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, finalize, from, mergeMap, tap } from 'rxjs';

import SettingsActions from '../actions';

@Injectable()
export class DigitalOddAcceptanceEffects {
    private loadingIndicatorHandler: Nullable<ILoadingIndicatorHandler> = null;
    private NOT_AUTHENTICATED_ERROR = 'NotAuthenticated';

    constructor(
        private actions: Actions,
        private loadingIndicatorService: CustomLoadingIndicatorService,
        private userSettings: SyncUserSettingsService,
        private userSettingsService: UserSettingsService,
        private redirectHelper: RedirectHelperService,
    ) {}

    handleRequestSetOddsAcceptance$ = createEffect(() => {
        return this.actions.pipe(
            ofType(SettingsActions.requestSetOddAcceptance),
            tap(() => {
                if (this.loadingIndicatorHandler === null) {
                    this.loadingIndicatorHandler = this.loadingIndicatorService.start();
                }
            }),
            mergeMap((action) => {
                return from(this.userSettingsService.set({ oddsAcceptanceMode: action.acceptance })).pipe(
                    mergeMap(() => [SettingsActions.setOddAcceptance({ acceptance: action.acceptance })]),
                    catchError((error) => {
                        if (error.message === this.NOT_AUTHENTICATED_ERROR) {
                            this.redirectHelper.goToLogin();
                        }
                        const defaultOddAcceptance =
                            (this.userSettings.bettingSettings.oddsAcceptanceMode as unknown as OddsAcceptanceMode) || OddsAcceptanceMode.No;

                        return [SettingsActions.setOddAcceptance({ acceptance: defaultOddAcceptance })];
                    }),
                    finalize(() => {
                        if (this.loadingIndicatorHandler !== null) {
                            this.loadingIndicatorHandler.done();
                            this.loadingIndicatorHandler = null;
                        }
                    }),
                );
            }),
        );
    });

    handleRequestSetAppNotification$ = createEffect(() => {
        return this.actions.pipe(
            ofType(SettingsActions.requestSetAppNotify),
            tap(() => {
                if (this.loadingIndicatorHandler === null) {
                    this.loadingIndicatorHandler = this.loadingIndicatorService.start();
                }
            }),
            mergeMap((action) => {
                return from(this.userSettingsService.set({ betslipAppNotification: action.value })).pipe(
                    mergeMap(() => [SettingsActions.setAppNotify({ value: action.value })]),
                    catchError((error) => {
                        if (error.message === this.NOT_AUTHENTICATED_ERROR) {
                            this.redirectHelper.goToLogin();
                        }
                        const defaultAppNotify = this.userSettings.bettingSettings.betslipAppNotification || false;

                        return [SettingsActions.setAppNotify({ value: defaultAppNotify })];
                    }),
                    finalize(() => {
                        if (this.loadingIndicatorHandler !== null) {
                            this.loadingIndicatorHandler.done();
                            this.loadingIndicatorHandler = null;
                        }
                    }),
                );
            }),
        );
    });

    handleRequestSetEmailNotification$ = createEffect(() => {
        return this.actions.pipe(
            ofType(SettingsActions.requestSetEmailNotify),
            tap(() => {
                if (this.loadingIndicatorHandler === null) {
                    this.loadingIndicatorHandler = this.loadingIndicatorService.start();
                }
            }),
            mergeMap((action) => {
                return from(this.userSettingsService.set({ betslipEmailNotification: action.value })).pipe(
                    mergeMap(() => [SettingsActions.setEmailNotify({ value: action.value })]),
                    catchError((error) => {
                        if (error.message === this.NOT_AUTHENTICATED_ERROR) {
                            this.redirectHelper.goToLogin();
                        }
                        const defaultEmailNotify = this.userSettings.bettingSettings.betslipEmailNotification || false;

                        return [SettingsActions.setEmailNotify({ value: defaultEmailNotify })];
                    }),
                    finalize(() => {
                        if (this.loadingIndicatorHandler !== null) {
                            this.loadingIndicatorHandler.done();
                            this.loadingIndicatorHandler = null;
                        }
                    }),
                );
            }),
        );
    });
}
